import { Media } from '@dreamstack/payload/src/payload-types'
import { cn, generateIdForText, normalizeHref } from '@dreamstack/util'
import type {
  DefaultNodeTypes,
  SerializedAutoLinkNode,
  SerializedHeadingNode,
  SerializedLinkNode,
  SerializedRelationshipNode,
  SerializedUploadNode,
} from '@payloadcms/richtext-lexical'
import {
  IS_BOLD,
  IS_ITALIC,
  IS_UNDERLINE,
} from '@payloadcms/richtext-lexical/lexical'
import {
  type JSXConvertersFunction,
  RichText,
} from '@payloadcms/richtext-lexical/react'
import Link from 'next/link'
import { createElement } from 'react'
import { EmbeddedAsset } from '../contentful/EmbeddedAsset'

export type RichText = {
  root: {
    type: string
    children: {
      type: any
      version: number
      [k: string]: unknown
    }[]
    direction: ('ltr' | 'rtl') | null
    format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | ''
    indent: number
    version: number
  }
  [k: string]: unknown
}

const getLinkProps = (
  node: SerializedLinkNode | SerializedAutoLinkNode
): { href: string; isInternal: boolean; isDocumentLink?: boolean } => {
  const { url } = node.fields
  // Autolinks don't have linkType - they're always external URLs
  const linkType = 'linkType' in node.fields ? node.fields.linkType : 'custom'

  if (linkType === 'internal') {
    const doc = 'doc' in node.fields ? node.fields.doc : undefined
    if (
      typeof doc?.value === 'object' &&
      !!doc.value?.url &&
      typeof doc.value.url === 'string'
    ) {
      return { href: doc.value.url, isInternal: true, isDocumentLink: true }
    }
    console.error('Internal link missing url:', doc)
    return { href: '#', isInternal: true }
  }

  // External/custom links - use url directly
  return { href: decodeURIComponent(url ?? '#'), isInternal: false }
}

const LinkRenderer: React.FC<{
  node: SerializedLinkNode | SerializedAutoLinkNode
  children: React.ReactNode
}> = ({ node, children }) => {
  const { href: rawHref, isInternal, isDocumentLink } = getLinkProps(node)
  const href = normalizeHref({ href: rawHref })
  const rel = node.fields.newTab ? 'noopener noreferrer' : undefined
  const target = node.fields.newTab ? '_blank' : undefined

  if (isInternal && !isDocumentLink) {
    return (
      <Link href={href} rel={rel} target={target}>
        {children}
      </Link>
    )
  }

  return (
    <a href={href} rel={rel} target={target}>
      {children}
    </a>
  )
}

const MediaRenderer: React.FC<{
  node: SerializedUploadNode | SerializedRelationshipNode
}> = ({ node }) => {
  if (node.relationTo !== 'media') {
    return null
  }

  if (!node.value || typeof node.value !== 'object') {
    return null
  }

  const mediaDoc = node.value as Media

  const url = mediaDoc.url
  if (!url) {
    return null
  }

  return <EmbeddedAsset asset={mediaDoc} />

  // const alt = mediaDoc.alt ?? mediaDoc.filename ?? ''
  // const isImage = mediaDoc.mimeType?.startsWith('image')

  // if (!isImage) {
  //   return (
  //     <a href={url} rel="noreferrer noopener" target="_blank">
  //       {mediaDoc.filename ?? url}
  //     </a>
  //   )
  // }

  // if (mediaDoc.width && mediaDoc.height) {
  //   return (
  //     <Image
  //       alt={alt}
  //       height={mediaDoc.height}
  //       src={url}
  //       width={mediaDoc.width}
  //     />
  //   )
  // }

  // return <Image unoptimized alt={alt} src={url} />
}

const getTextFromSerializedNodes = (
  nodes: SerializedHeadingNode['children']
): string => {
  return nodes
    .map((node) => {
      if (
        node.type === 'text' &&
        'text' in node &&
        typeof node.text === 'string'
      ) {
        return node.text
      }
      if ('children' in node && Array.isArray(node.children)) {
        return getTextFromSerializedNodes(node.children)
      }
      return ''
    })
    .join('')
}

const HeadingRenderer: React.FC<{
  node: SerializedHeadingNode
  children: React.ReactNode
}> = ({ node, children }) => {
  const tag = node.tag
  const text = getTextFromSerializedNodes(node.children)
  const id = generateIdForText(text)

  if (text.trim() === '') {
    return createElement(tag, {}, children)
  }
  return createElement(tag, { id }, children)
}

const jsxConverters: JSXConvertersFunction<DefaultNodeTypes> = ({
  defaultConverters,
}) => ({
  ...defaultConverters,
  link: ({ node, nodesToJSX }) => (
    <LinkRenderer node={node}>
      {nodesToJSX({ nodes: node.children })}
    </LinkRenderer>
  ),
  autolink: ({ node, nodesToJSX }) => (
    <LinkRenderer node={node}>
      {nodesToJSX({ nodes: node.children })}
    </LinkRenderer>
  ),
  upload: ({ node }) => <MediaRenderer node={node} />,
  relationship: ({ node }) => <MediaRenderer node={node} />,
  heading: ({ node, nodesToJSX }) => (
    <HeadingRenderer node={node}>
      {nodesToJSX({ nodes: node.children })}
    </HeadingRenderer>
  ),
  text: ({ node, nodesToJSX }) => {
    if (node.format & IS_BOLD) {
      return (
        <span className="font-semibold">
          <span>{node.text}</span>
        </span>
      )
    }

    if (node.format & IS_ITALIC) {
      return (
        <i>
          <span>{node.text}</span>
        </i>
      )
    }

    if (node.format & IS_UNDERLINE) {
      return (
        <u>
          <span>{node.text}</span>
        </u>
      )
    }

    return <span>{node.text}</span>
  },
  list: ({ node, nodesToJSX }) => {
    const Tag = node.listType === 'number' ? 'ol' : 'ul'
    return <Tag>{nodesToJSX({ nodes: node.children })}</Tag>
  },
  listitem: ({ node, nodesToJSX }) => (
    <li>{nodesToJSX({ nodes: node.children })}</li>
  ),
})

export const SuperRichTextWrapper = ({
  textAlignCenter,
  blackBackground,
  children,
}: {
  textAlignCenter?: boolean | null
  blackBackground?: boolean | null
  children: React.ReactNode
}) => {
  return (
    <div
      className={cn(
        'whitespace-pre-line w-full',
        textAlignCenter && 'text-center',
        blackBackground
          ? 'prose max-md:prose-accentro-white md:prose-accentro-white-md ml:prose-accentro-white-ml'
          : 'prose max-md:prose-accentro md:prose-accentro-md ml:prose-accentro-ml'
      )}
    >
      {children}
    </div>
  )
}

export const SuperRichText = ({
  richText,
  blackBackground,
  textAlignCenter,
}: {
  richText: RichText
  blackBackground?: boolean | null
  textAlignCenter?: boolean | null
}) => {
  if (!richText) return null

  return (
    <SuperRichTextWrapper
      textAlignCenter={textAlignCenter}
      blackBackground={blackBackground}
    >
      <RichText data={richText} converters={jsxConverters} disableContainer />
    </SuperRichTextWrapper>
  )
}
